import { http } from "@/lib/api";

export type GuideStatus = "Waiting" | "Apply" | "Approved" | "Rejected" | "Scored" | "Leveled";
export type GuideStage = "Register" | "Sharing" | "Evaluation" | "Publish";
export type GuideOperation =
  | "submit"
  | "approve"
  | "reject"
  | "resubmit"
  | "request_rectify"
  | "submit_rectify"
  | "final_approve";

export interface GuideListItem {
  id: string; // 雪花 BIGINT,后端序列化为字符串(> Number.MAX_SAFE_INTEGER)
  reg_no: string;
  title: string;
  title_en: string | null;
  type: string | null;
  register: string | null;
  release_org: string | null;
  release_date: string | null;
  subject_classification: string | null;
  score: number | null;
  level: string | null;
  status: GuideStatus;
  stage: GuideStage;
  created_at: string;
  // 门户列表标签行(版本/期刊/截止日期)与评分可见性(后端 P0.3 补充暴露)
  edition: string | null;
  journal: string | null;
  end_date: string | null;
  show_score: boolean;
}

export interface GuideListResp {
  items: GuideListItem[];
  total: number;
  page: number;
  page_size: number;
}

/** 筛选面板各列取值计数(key 为列原始值),GET /guides/facets */
export interface GuideFacets {
  type: Record<string, number>;
  subject_classification: Record<string, number>;
  population_classification: Record<string, number>;
  edition: Record<string, number>;
  level: Record<string, number>;
}

export interface GuideDetail extends GuideListItem {
  edition: string | null;
  country: string | null;
  population_classification: string | null;
  journal: string | null;
  origin_link: string | null;
  pdf_url: string | null;
  // 门户详情页「介绍」块(后端 P0.7 补充暴露;end_date 已在 GuideListItem)
  start_date: string | null;
  source_of_funds: string | null;
  files: Array<Record<string, unknown>>;
  user_id: string | null;
  submit_time: string | null;
  evaluation_count: number;
  allowed_operations: GuideOperation[];
  // 业务字段
  keywords: string | null;
  abstract: string | null;
  language: string | null;
  clinical_question: string | null;
  target_population: string | null;
  recommendation_count: number | null;
  evidence_grade: string | null;
  consensus_method: string | null;
  funding_amount_cny: string | null;
  coi_declared: string | null;
  // 动态扩展字段值(P1.4):key 为 guide_fields.field
  extra_fields: Record<string, unknown>;
  // 创建钩子(2.6 查重):仅创建时可能非空
  duplicate_warnings?: DuplicateWarnings | null;
}

export interface GuideCreateIn {
  title: string;
  title_en?: string;
  type?: string;
  edition?: string;
  register?: string;
  release_org?: string;
  release_date?: string;
  country?: string;
  subject_classification?: string;
  population_classification?: string;
  journal?: string;
  origin_link?: string;
  pdf_url?: string;
  files?: Array<Record<string, unknown>>;
  // 业务字段(物理列)
  keywords?: string;
  abstract?: string;
  language?: "zh" | "en" | "mixed";
  clinical_question?: string;
  target_population?: string;
  recommendation_count?: number;
  evidence_grade?: "1A" | "1B" | "2A" | "2B" | "3";
  consensus_method?: "delphi" | "nominal_group" | "informal" | "none";
  funding_amount_cny?: number;
  coi_declared?: "yes" | "partial" | "no";
  // 动态扩展字段值(P1.4)
  extra_fields?: Record<string, unknown>;
}

export function listGuides(params: {
  page?: number;
  page_size?: number;
  q?: string;
  status?: GuideStatus[];
  stage?: GuideStage[];
  country?: string;
  subject_classification?: string;
  population_classification?: string;
  type?: string;
  edition?: string;
  level?: string;
  mine?: boolean;
  registered?: boolean;
  show_score?: boolean;
  sort?: "created_at" | "score" | "release_date";
  order?: "asc" | "desc";
}): Promise<GuideListResp> {
  return http("/guides", { query: params });
}

/** 筛选面板计数:只传三态作用域参数(q/status/registered/mine 等),不传面板列勾选。 */
export function getGuideFacets(params: {
  q?: string;
  status?: GuideStatus[];
  stage?: GuideStage[];
  country?: string;
  mine?: boolean;
  registered?: boolean;
}): Promise<GuideFacets> {
  return http("/guides/facets", { query: params });
}

export function getGuide(id: string): Promise<GuideDetail> {
  return http(`/guides/${id}`);
}

/** 相似指南推荐(P3.6,GET /guides/{id}/similar,公开):top5,向量优先 + 标题/学科兜底 */
export function getSimilarGuides(id: string): Promise<SimilarGuideItem[]> {
  return http(`/guides/${id}/similar`);
}

// ===== AI 评审(门户详情页 P3.1,与 admin/api.ts 同契约,门户侧自持有)=====

export type AIReviewStatus =
  | "queued"
  | "parsing"
  | "scoring"
  | "done"
  | "failed"
  | "budget_exceeded";

export interface EvidenceQuote {
  text: string;
  page: number | null;
}

export interface AIReviewDimOut {
  dim_code: string;
  /** 0-10,后端 Decimal 序列化为字符串 */
  score: string | null;
  /** 0-1 */
  confidence: string | null;
  risk_flags: string[] | null;
  reasoning_steps: string[] | null;
  evidence_quotes: EvidenceQuote[] | null;
}

export interface AIReviewRunOut {
  id: string;
  guide_id: string;
  pdf_sha256: string;
  prompt_version: string;
  model_primary: string;
  status: AIReviewStatus;
  cost_cny: string | null;
  started_at: string | null;
  finished_at: string | null;
  error_message: string | null;
  promoted_to_human: boolean;
  dims: AIReviewDimOut[];
}

/** 某指南的 AI 评审 run 历史(GET /ai-reviews?guide_id=),按开始时间倒序 */
export function listAIReviewRuns(guideId: string, limit = 20): Promise<AIReviewRunOut[]> {
  return http("/ai-reviews", { query: { guide_id: guideId, limit } });
}

export function createGuide(body: GuideCreateIn): Promise<GuideDetail> {
  return http("/guides", { method: "POST", body });
}

/** 查重相似条目(2.6,POST /guides/check-duplicate) */
export interface SimilarGuideItem {
  guide_id: string;
  reg_no: string;
  title: string;
  title_en: string | null;
  subject_classification: string | null;
  status: GuideStatus;
  /** 综合相似度 0-1:max(向量 cosine, 标题 trgm) */
  score: number;
  matched_on: Array<"title" | "vector" | "subject">;
}

export interface DuplicateCheckResp {
  items: SimilarGuideItem[];
  threshold: number;
  degraded: boolean;
}

/** 创建钩子回执(POST /guides 响应可选字段) */
export interface DuplicateWarnings {
  count: number;
  threshold: number;
  top: SimilarGuideItem[];
}

/** 新建指南查重(需求 #19,提示性质):阈值之上的相似指南列表 */
export function checkDuplicateGuides(body: {
  title: string;
  title_en?: string;
  subject_classification?: string;
  abstract?: string;
  keywords?: string;
}): Promise<DuplicateCheckResp> {
  return http("/guides/check-duplicate", { method: "POST", body });
}

export function operateGuide(
  id: string,
  operation: GuideOperation,
  remark?: string,
): Promise<GuideDetail> {
  return http(`/guides/${id}/operate`, {
    method: "POST",
    body: { operation, remark },
  });
}

export const STATUS_LABELS: Record<GuideStatus, string> = {
  Waiting: "待提交",
  Apply: "已申请",
  Approved: "已批准",
  Rejected: "已驳回",
  Scored: "已评分",
  Leveled: "已定级",
};
export const STATUS_COLORS: Record<GuideStatus, string> = {
  Waiting: "default",
  Apply: "processing",
  Approved: "success",
  Rejected: "error",
  Scored: "blue",
  Leveled: "gold",
};

export const STAGE_LABELS: Record<GuideStage, string> = {
  Register: "注册",
  Sharing: "共享",
  Evaluation: "评价",
  Publish: "发布",
};

export const OP_LABELS: Record<GuideOperation, string> = {
  submit: "提交",
  approve: "审核通过",
  reject: "驳回",
  resubmit: "重新提交",
  request_rectify: "要求整改",
  submit_rectify: "提交整改",
  final_approve: "终审通过",
};
